import React, { useEffect, useState } from "react";
import "./HomeBanner.css";
import AnimationLogo from "../../assets/images/Animation-logo.png";
import LogoImg from "../../assets/images/navbar-logo.png";
import {
  MdOutlineKeyboardArrowDown,
  MdKeyboardArrowRight,
  MdMenu,
  MdClose,
} from "react-icons/md";
import LanguageSelector from "../../LanguageSelector";

export default function HomeBanner({ currentLanguage = "en", onLanguageChange }) {
  const [openServices, setOpenServices] = useState(false);
  const [openIndustries, setOpenIndustries] = useState(false);
  const [hoverServices, setHoverServices] = useState(null);
  const [hoverIndustries, setHoverIndustries] = useState(null);

  // Drawer state + accordion states for mobile
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [servicesAccordionOpen, setServicesAccordionOpen] = useState(false);
  const [industriesAccordionOpen, setIndustriesAccordionOpen] = useState(false);
  const [openServiceIndex, setOpenServiceIndex] = useState(null);
  const [openIndustryIndex, setOpenIndustryIndex] = useState(null);

  const clean = (txt) => txt.toLowerCase().replace(/[^a-z0-9]+/g, "");

  const isMobile = () =>
    typeof window !== "undefined" && window.innerWidth <= 991;

  useEffect(() => {
    // lock body scroll when drawer is open
    if (typeof document !== "undefined") {
      document.body.style.overflow = drawerOpen ? "hidden" : "";
    }
    return () => {
      if (typeof document !== "undefined") document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  // ------------------ TRANSLATIONS ------------------
  const translations = {
    en: {
      home: "Home",
      aboutUs: "About Us",
      services: "Services",
      industries: "Industries We Serve",
      insight: "Insight",
      career: "Career",
      contactUs: "Contact Us",
      title: "EMPOWERING BUSINESSES THROUGH FINANCIAL INTELLIGENCE",
      subtitle: "Pestel & Partners brings human understanding and financial expertise together to help clients move forward with clarity and confidence.",
      cta: "Get a Consultation",
      scroll: "Scroll Down To Explore",
      servicesMenu: [
        {
          title: "Audit and Assurance",
          children: [
            "Financial Statement Audit",
            "IFRS Advisory Service",
            "Other Assurance Service",
            "Internal Control Framework",
          ],
        },
        {
          title: "Advisory",
          children: [
            "Deal Advisory",
            "Restructuring",
            "Due Diligence",
            "Forensic Services",
            "Risk Advisory",
            "Technology Advisory Service",
            "Valuations",
          ],
        },
        {
          title: "Taxation",
          children: [
            "Corporate Tax",
            "Merger and Acquisition",
            "Transfer Pricing",
            "Value Added Tax",
            "International Tax",
            "Excise Tax",
            "Tax Restructuring",
            "Economic Substance Regulations",
          ],
        },
        {
          title: "Business Service Outsourcing",
          children: [
            "Finance and Accounting",
            "Corporate Advisory Services",
            "Expenses Claim Management",
            "Payroll & HR Services",
            "Secondment",
            "Treasury Service",
          ],
        },
        {
          title: "Strategies",
          children: ["Market Research", "Business Consulting"],
        },
        {
          title: "Corporate Services",
          children: [
            "Entity Set-up",
            "Outsourced Finance & Regulatory Compliance",
            "Company Secertarial",
          ],
        },
      ],
      industriesMenu: [
        {
          title: "Natural Resources and Energy",
          children: [
            "Oil and Gas",
            "Power and Utilities",
            "Waste Management",
            "Mining and Minerals",
          ],
        },
        {
          title: "Financial Services",
          children: [
            "Asset Management",
            "Banking",
            "Financial Advisory Services",
            "Financial Service Tax",
            "Fintech",
            "Insurance",
          ],
        },
        { title: "Health Care", children: [] },
        { title: "Life Science", children: [] },
        { title: "Private Equity", children: [] },
        { title: "Professional Service", children: [] },
        { title: "Public Sector", children: [] },
        { title: "Real Estate & Construction", children: [] },
        {
          title: "Technology, Media, Entertainment & Telecommunications",
          children: ["Media and Entertainment", "Technology", "Telecommunication"],
        },
      ],
    },
    fr: {
      home: "Accueil",
      aboutUs: "À Propos",
      services: "Services",
      industries: "Secteurs Que Nous Servons",
      insight: "Perspectives",
      career: "Carrière",
      contactUs: "Contactez-Nous",
      title: "AUTONOMISER LES ENTREPRISES PAR L'INTELLIGENCE FINANCIÈRE",
      subtitle: "Pestel & Partners rassemble la compréhension humaine et l'expertise financière pour aider les clients à avancer avec clarté et confiance.",
      cta: "Obtenir une Consultation",
      scroll: "Faites Défiler Pour Explorer",
      servicesMenu: [
        {
          title: "Audit et Assurance",
          children: [
            "Audit des États Financiers",
            "Service Conseil IFRS",
            "Autre Service d'Assurance",
            "Cadre de Contrôle Interne",
          ],
        },
        {
          title: "Conseil",
          children: [
            "Conseil en Transaction",
            "Restructuration",
            "Due Diligence",
            "Services Forensiques",
            "Conseil en Risque",
            "Service Conseil en Technologie",
            "Évaluations",
          ],
        },
        {
          title: "Fiscalité",
          children: [
            "Impôt sur les Sociétés",
            "Fusion et Acquisition",
            "Prix de Transfert",
            "Taxe sur la Valeur Ajoutée",
            "Fiscalité Internationale",
            "Taxe d'Accise",
            "Restructuration Fiscale",
            "Réglementations sur la Substance Économique",
          ],
        },
        {
          title: "Externalisation de Services aux Entreprises",
          children: [
            "Finance et Comptabilité",
            "Services Conseil aux Entreprises",
            "Gestion des Demandes de Remboursement",
            "Services Paie & RH",
            "Détachement",
            "Service de Trésorerie",
          ],
        },
        {
          title: "Stratégies",
          children: ["Étude de Marché", "Conseil en Affaires"],
        },
        {
          title: "Services aux Entreprises",
          children: [
            "Création d'Entité",
            "Finance Externalisée & Conformité Réglementaire",
            "Secrétariat d'Entreprise",
          ],
        },
      ],
      industriesMenu: [
        {
          title: "Ressources Naturelles et Énergie",
          children: [
            "Pétrole et Gaz",
            "Électricité et Services Publics",
            "Gestion des Déchets",
            "Mines et Minéraux",
          ],
        },
        {
          title: "Services Financiers",
          children: [
            "Gestion d'Actifs",
            "Banque",
            "Services Conseil Financiers",
            "Fiscalité des Services Financiers",
            "Fintech",
            "Assurance",
          ],
        },
        { title: "Santé", children: [] },
        { title: "Sciences de la Vie", children: [] },
        { title: "Capital-Investissement", children: [] },
        { title: "Service Professionnel", children: [] },
        { title: "Secteur Public", children: [] },
        { title: "Immobilier et Construction", children: [] },
        {
          title: "Technologie, Médias, Divertissement et Télécommunications",
          children: ["Médias et Divertissement", "Technologie", "Télécommunication"],
        },
      ],
    },
  };

  const t = translations[currentLanguage] || translations.en;

  // Get the appropriate menu data
  const servicesMenu = t.servicesMenu;
  const industriesMenu = t.industriesMenu;

  // Always use English titles for URL generation
  const getEnglishTitle = (title, menuType) => {
    const englishMenus = translations.en[menuType];
    const currentMenus = t[menuType];
    
    const index = currentMenus.findIndex(item => item.title === title);
    if (index !== -1 && englishMenus[index]) {
      return englishMenus[index].title;
    }
    return title;
  };

  const getEnglishChildTitle = (parentTitle, childTitle, menuType) => {
    const englishMenus = translations.en[menuType];
    const currentMenus = t[menuType];
    
    const parentIndex = currentMenus.findIndex(item => item.title === parentTitle);
    if (parentIndex !== -1 && englishMenus[parentIndex]) {
      const childIndex = currentMenus[parentIndex].children.findIndex(child => child === childTitle);
      if (childIndex !== -1 && englishMenus[parentIndex].children[childIndex]) {
        return englishMenus[parentIndex].children[childIndex];
      }
    }
    return childTitle;
  };

  const makePath = (base, parent, child = null, menuType) => {
    const englishParent = getEnglishTitle(parent, menuType);
    if (child) {
      const englishChild = getEnglishChildTitle(parent, child, menuType);
      return `/${base}/${clean(englishParent)}/${clean(englishChild)}`;
    }
    return `/${base}/${clean(englishParent)}`;
  };

  // handlers for drawer and accordion
  const toggleDrawer = () => setDrawerOpen((s) => !s);
  const closeDrawer = () => setDrawerOpen(false);

  const toggleServicesAccordion = () => {
    setServicesAccordionOpen((s) => !s);
    if (servicesAccordionOpen) setOpenServiceIndex(null);
  };
  const toggleIndustriesAccordion = () => {
    setIndustriesAccordionOpen((s) => !s);
    if (industriesAccordionOpen) setOpenIndustryIndex(null);
  };

  // Smooth scroll to next section
  const handleScrollDown = () => {
    const nextSection = document.getElementById("next-section");
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  const changeLanguage = (lang) => {
    if (onLanguageChange) {
      onLanguageChange(lang);
    }
  };

  return (
    <div className="banner-animation-wrapper">
      <div className="banner-logo-intro">
        <img src={AnimationLogo} alt="Logo" className="intro-logo" />
      </div>

      <section className="homebanner-section">
        {/* Background Video */}
        <video className="homebanner-video" autoPlay loop muted playsInline>
          <source
            src="https://blueberrydatastorage.blob.core.windows.net/websitesdata/PestelPartners/HomeBanner.webm"
            type="video/webm"
          />
        </video>

        {/* TOP BAR */}
        <div className="homebanner-top">
          <div className="homebanner-row row align-items-center justify-content-between">
            <div className="col-md-3 english-div">
              <LanguageSelector
                changeLanguage={changeLanguage}
                currentLanguage={currentLanguage}
                renderTrigger={(lang) => (
                  <div className="d-flex align-items-center home-lang-box top-left">
                    <img
                      src={lang.flagImg}
                      className="lang-flag"
                      alt={lang.name}
                    />
                    <span className="lang-text">{lang.name}</span>
                    <MdOutlineKeyboardArrowDown size={20} color="white" />
                  </div>
                )}
              />
            </div>

            <div className="col-md-6 d-flex justify-content-center top-center">
              <img src={LogoImg} alt="Logo" className="top-logo" />
            </div>
            <div className="col-md-3 contact-us-div">
              <div className="d-flex align-items-center home-contact-box top-right">
                <a href="/contact">
                  <span>{t.contactUs}</span>
                </a>
                <img
                  src="/assets/icons/RightArrow.png"
                  className="arrow-icon"
                  alt="arrow"
                />
              </div>
            </div>
          </div>
          <div className="top-separator" />
        </div>

        <div className="homebanner-overlay" />

        {/* HAMBURGER BUTTON */}
        <button
          className={`hamburger-btn ${drawerOpen ? "open" : ""}`}
          aria-label={drawerOpen ? "Close menu" : "Open menu"}
          onClick={toggleDrawer}
        >
          {drawerOpen ? <MdClose size={28} /> : <MdMenu size={28} />}
        </button>

        {/* NAV (desktop) */}
        <nav className="homebanner-nav">
          <ul>
            <li>
              <a href="/" className="hb-link">
                {t.home}
              </a>
            </li>
            <li>
              <a href="/about" className="hb-link">
                {t.aboutUs}
              </a>
            </li>

            {/* SERVICES (desktop dropdown) */}
            <li
              className="dropdown-parent"
              onMouseEnter={() => !isMobile() && setOpenServices(true)}
              onMouseLeave={() =>
                !isMobile() && (setOpenServices(false), setHoverServices(null))
              }
              onClick={() => isMobile() && setOpenServices(!openServices)}
            >
              <span className="nav-dropdown-toggle hb-link">
                {t.services} <MdOutlineKeyboardArrowDown />
              </span>

              {openServices && <div className="dropdown-bridge" />}

              {openServices && (
                <div className="dropdown-box banner-tight">
                  {servicesMenu.map((item, index) => (
                    <div
                      key={index}
                      className={`dropdown-item-row ${
                        hoverServices === index ? "active-parent" : ""
                      }`}
                      onMouseEnter={() =>
                        !isMobile() && setHoverServices(index)
                      }
                      onClick={() =>
                        isMobile() &&
                        setHoverServices(hoverServices === index ? null : index)
                      }
                    >
                      <a
                        href={
                          item.children.length === 0
                            ? makePath("services", item.title, null, "servicesMenu")
                            : undefined
                        }
                        onClick={(e) => {
                          if (item.children.length > 0) {
                            e.preventDefault();
                          }
                        }}
                        className={`hb-parent-link ${
                          item.children.length === 0
                            ? "clickable"
                            : "not-clickable"
                        }`}
                      >
                        {item.title}
                      </a>

                      {item.children.length > 0 && (
                        <MdKeyboardArrowRight size={20} />
                      )}

                      {hoverServices === index && item.children.length > 0 && (
                        <div className="sub-dropdown-box banner-tight">
                          {item.children.map((child, cIdx) => (
                            <a
                              key={cIdx}
                              href={makePath("services", item.title, child, "servicesMenu")}
                              className="sub-dropdown-item"
                            >
                              {child}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </li>

            {/* INDUSTRIES (desktop dropdown) */}
            <li
              className="dropdown-parent"
              onMouseEnter={() => !isMobile() && setOpenIndustries(true)}
              onMouseLeave={() =>
                !isMobile() &&
                (setOpenIndustries(false), setHoverIndustries(null))
              }
              onClick={() => isMobile() && setOpenIndustries(!openIndustries)}
            >
              <span className="nav-dropdown-toggle hb-link">
                {t.industries} <MdOutlineKeyboardArrowDown />
              </span>

              {openIndustries && <div className="dropdown-bridge" />}

              {openIndustries && (
                <div className="dropdown-box banner-tight">
                  {industriesMenu.map((item, index) => (
                    <div
                      key={index}
                      className={`dropdown-item-row ${
                        hoverIndustries === index ? "active-parent" : ""
                      }`}
                      onMouseEnter={() =>
                        !isMobile() && setHoverIndustries(index)
                      }
                      onClick={() =>
                        isMobile() &&
                        setHoverIndustries(
                          hoverIndustries === index ? null : index
                        )
                      }
                    >
                      <a
                        href={
                          item.children.length === 0
                            ? makePath("industries", item.title, null, "industriesMenu")
                            : undefined
                        }
                        onClick={(e) => {
                          if (item.children.length > 0) {
                            e.preventDefault();
                          }
                        }}
                        className={`hb-parent-link ${
                          item.children.length === 0
                            ? "clickable"
                            : "not-clickable"
                        }`}
                      >
                        {item.title}
                      </a>

                      {item.children.length > 0 && (
                        <MdKeyboardArrowRight size={20} />
                      )}

                      {hoverIndustries === index &&
                        item.children.length > 0 && (
                          <div className="sub-dropdown-box banner-tight">
                            {item.children.map((child, cIdx) => (
                              <a
                                key={cIdx}
                                href={makePath("industries", item.title, child, "industriesMenu")}
                                className="sub-dropdown-item"
                              >
                                {child}
                              </a>
                            ))}
                          </div>
                        )}
                    </div>
                  ))}
                </div>
              )}
            </li>

            <li>
              <a href="/blog" className="hb-link">
                {t.insight}
              </a>
            </li>
            <li>
              <a href="/careers" className="hb-link">
                {t.career}
              </a>
            </li>
          </ul>
        </nav>

        {/* HERO CONTENT */}
        <div className="homebanner-content">
          <h1 className="homebanner-title">{t.title}</h1>

          <p className="homebanner-subtitle">{t.subtitle}</p>

          <button
            className="home-cta-btn"
            onClick={() => (window.location.href = "/contact")}
          >
            <span>{t.cta}</span>
            <img
              src="/assets/icons/RightArrow.png"
              className="arrow-icon"
              alt="arrow"
            />
          </button>
        </div>

        <div
          className="homebanner-scroll"
          onClick={handleScrollDown}
          style={{ cursor: "pointer" }}
        >
          <div className="scroll-arrow" />
          <span>{t.scroll}</span>
        </div>

        {/* Mobile/Tablet Right Drawer */}
        <div
          className={`hb-drawer-overlay ${drawerOpen ? "visible" : ""}`}
          onClick={closeDrawer}
          role="presentation"
        />

        <aside
          className={`hb-drawer ${drawerOpen ? "open" : ""}`}
          aria-hidden={!drawerOpen}
        >
          <div className="hb-drawer-inner">
            <div className="hb-drawer-header">
              <img src={LogoImg} className="hb-drawer-logo" alt="logo" />
              <button
                className="hb-drawer-close"
                onClick={closeDrawer}
                aria-label="Close menu"
              >
                <MdClose size={24} />
              </button>
            </div>

            <nav className="hb-drawer-nav" aria-label="Mobile navigation">
              <ul>
                <li>
                  <a href="/" onClick={closeDrawer}>
                    {t.home}
                  </a>
                </li>
                <li>
                  <a href="/about" onClick={closeDrawer}>
                    {t.aboutUs}
                  </a>
                </li>

                {/* Services accordion */}
                <li className="hb-accordion-item">
                  <button
                    className="hb-accordion-toggle"
                    onClick={toggleServicesAccordion}
                    aria-expanded={servicesAccordionOpen}
                  >
                    {t.services}
                    <span className="hb-accordion-icon">
                      {servicesAccordionOpen ? "▴" : "▾"}
                    </span>
                  </button>

                  {servicesAccordionOpen && (
                    <div className="hb-accordion-panel">
                      {servicesMenu.map((s, idx) => (
                        <div key={idx} className="hb-acc-row">
                          <button
                            className="hb-acc-parent"
                            onClick={() => {
                              if (s.children.length === 0) {
                                window.location.href = makePath(
                                  "services",
                                  s.title,
                                  null,
                                  "servicesMenu"
                                );
                                closeDrawer();
                              } else {
                                setOpenServiceIndex(
                                  openServiceIndex === idx ? null : idx
                                );
                              }
                            }}
                          >
                            {s.title}
                            <span>{openServiceIndex === idx ? "▴" : "▸"}</span>
                          </button>

                          {openServiceIndex === idx &&
                            s.children.length > 0 && (
                              <div className="hb-acc-children">
                                {s.children.map((c, ci) => (
                                  <a
                                    key={ci}
                                    href={makePath("services", s.title, c, "servicesMenu")}
                                    onClick={closeDrawer}
                                    className="hb-acc-child-link"
                                  >
                                    {c}
                                  </a>
                                ))}
                              </div>
                            )}
                        </div>
                      ))}
                    </div>
                  )}
                </li>

                {/* Industries accordion */}
                <li className="hb-accordion-item">
                  <button
                    className="hb-accordion-toggle"
                    onClick={toggleIndustriesAccordion}
                    aria-expanded={industriesAccordionOpen}
                  >
                    {t.industries}
                    <span className="hb-accordion-icon">
                      {industriesAccordionOpen ? "▴" : "▾"}
                    </span>
                  </button>

                  {industriesAccordionOpen && (
                    <div className="hb-accordion-panel">
                      {industriesMenu.map((s, idx) => (
                        <div key={idx} className="hb-acc-row">
                          <button
                            className="hb-acc-parent"
                            onClick={() => {
                              if (s.children.length === 0) {
                                window.location.href = makePath(
                                  "industries",
                                  s.title,
                                  null,
                                  "industriesMenu"
                                );
                                closeDrawer();
                              } else {
                                setOpenIndustryIndex(
                                  openIndustryIndex === idx ? null : idx
                                );
                              }
                            }}
                          >
                            {s.title}
                            <span>
                              {s.children.length > 0
                                ? openIndustryIndex === idx
                                  ? "▴"
                                  : "▸"
                                : ""}
                            </span>
                          </button>

                          {openIndustryIndex === idx &&
                            s.children.length > 0 && (
                              <div className="hb-acc-children">
                                {s.children.map((c, ci) => (
                                  <a
                                    key={ci}
                                    href={makePath("industries", s.title, c, "industriesMenu")}
                                    onClick={closeDrawer}
                                    className="hb-acc-child-link"
                                  >
                                    {c}
                                  </a>
                                ))}
                              </div>
                            )}
                        </div>
                      ))}
                    </div>
                  )}
                </li>

                <li>
                  <a href="/blog" onClick={closeDrawer}>
                    {t.insight}
                  </a>
                </li>
                <li>
                  <a href="/careers" onClick={closeDrawer}>
                    {t.career}
                  </a>
                </li>

                {/* Contact quick link */}
                <li className="hb-contact-item">
                  <a
                    href="/contact"
                    className="hb-contact-btn"
                    onClick={closeDrawer}
                  >
                    {t.contactUs}
                  </a>
                </li>
              </ul>
            </nav>
          </div>
        </aside>
      </section>
    </div>
  );
}